import AppLayout from '../layouts/AppLayout'
import React, { useState, useEffect, useContext, useCallback } from 'react'
import { AppContext } from '../contexts/AppContext'
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { HiArrowNarrowRight } from 'react-icons/hi'
import WorkCover from '../components/WorkCover'
import { WORK_STATUSES, LANGUAGES, currentPeriodLabel } from '../constants/domain'

const today = () => new Date().toISOString().split('T')[0]

// Add or edit a Work (audio title). Stored as a row in `matches`.
const AddMatch = () => {
  const { matchId } = useParams()

  const [competition, setCompetition] = useState('')
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState('')
  const [language, setLanguage] = useState('English')
  const [episodes, setEpisodes] = useState('')
  const [date, setDate] = useState(today())
  const [matchday, setMatchday] = useState(currentPeriodLabel())
  const [status, setStatus] = useState('Monitoring')
  const [existingCover, setExistingCover] = useState(null)
  const [cover, setCover] = useState(null)
  const [coverPreview, setCoverPreview] = useState(null)
  const [error, setError] = useState('')
  const [competitions, setCompetitions] = useState([])

  const { getmatch, addmatch, updatematch, isLoading, getCompetitions } = useContext(AppContext)

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response || [])
  }, [getCompetitions])

  useEffect(() => {
    const fetchWork = async () => {
      if (matchId) {
        const response = await getmatch(matchId)
        if (response && response.length > 0) {
          const work = response[0]
          setCompetition(work.competition)
          setTitle(work.title || '')
          setAuthor(work.author || '')
          setLanguage(work.language || 'English')
          setEpisodes(work.episodes ?? '')
          setDate(work.date)
          setMatchday(work.matchday || '')
          setStatus(work.status)
          setExistingCover(work.cover)
        }
      }
    }

    fetchWork()
  }, [matchId])

  useEffect(() => {
    fetchCompetitions()
  }, [fetchCompetitions])

  useEffect(() => {
    if (!cover) {
      setCoverPreview(null)
      return
    }
    const url = URL.createObjectURL(cover)
    setCoverPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [cover])

  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isLoading) return
    if (competition === '' || title.trim() === '' || date === '' || matchday.trim() === '') {
      setError('Category, title, monitoring start date and reporting period are required')
      return
    }
    setError('')
    try {
      const work = {
        competition,
        title: title.trim(),
        author: author.trim(),
        language,
        episodes,
        date,
        time: '',
        matchday: matchday.trim(),
        status,
        cover,
      }
      const response = matchId ? await updatematch({ ...work, matchid: matchId }) : await addmatch(work)
      if (response === 'success') {
        navigate('/all-matches')
      }
    } catch (error) {
      setError('Error: ' + error)
    }
  }

  return (
    <AppLayout pageName={matchId ? 'Update Work' : 'Add New Work'}>
      <section>
        <form
          onSubmit={handleSubmit}
          method='post'
          encType='multipart/form-data'
          className='flex flex-row flex-wrap w-full'
        >
          <div className='w-full md:w-1/4 p-2 flex flex-col items-center gap-3'>
            <WorkCover
              title={title || 'New Work'}
              cover={existingCover}
              src={coverPreview}
              size={180}
            />
            <label
              htmlFor='cover'
              className='btn-submit cursor-pointer'
            >
              <span>{existingCover || cover ? 'Change cover' : 'Upload cover'}</span>
            </label>
            <input
              id='cover'
              name='cover'
              type='file'
              accept='image/*'
              className='hidden'
              onChange={(e) => setCover(e.target.files[0] || null)}
            />
          </div>

          <div className='w-full md:w-3/4 flex flex-row flex-wrap'>
            <div className='w-full p-2'>
              <label htmlFor='title'>Title</label>
              <input
                id='title'
                name='title'
                type='text'
                required
                placeholder='e.g. My Contract Husband'
                className='form-control'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='author'>Author / Narrator</label>
              <input
                id='author'
                name='author'
                type='text'
                className='form-control'
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='competition'>Category</label>
              <select
                id='competition'
                name='competition'
                className='form-control'
                value={competition}
                onChange={(e) => setCompetition(e.target.value)}
                required
              >
                <option
                  disabled
                  value=''
                >
                  Select
                </option>
                {competitions.map((cmp) => (
                  <option
                    key={cmp.id}
                    value={cmp.id}
                  >
                    {cmp.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='language'>Language</label>
              <select
                id='language'
                name='language'
                className='form-control'
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {LANGUAGES.map((lang) => (
                  <option
                    key={lang}
                    value={lang}
                  >
                    {lang}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='episodes'>Episodes (optional)</label>
              <input
                id='episodes'
                name='episodes'
                type='number'
                min='0'
                className='form-control'
                value={episodes}
                onChange={(e) => setEpisodes(e.target.value)}
              />
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='date'>Monitoring start date</label>
              <input
                id='date'
                name='date'
                required
                type='date'
                className='form-control'
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='matchday'>Reporting period</label>
              <input
                id='matchday'
                name='matchday'
                type='text'
                required
                maxLength={10}
                placeholder='e.g. Week 43'
                className='form-control'
                value={matchday}
                onChange={(e) => setMatchday(e.target.value)}
              />
            </div>
            <div className='w-2/4 p-2'>
              <label htmlFor='status'>Status</label>
              <select
                id='status'
                name='status'
                className='form-control'
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {WORK_STATUSES.map((s) => (
                  <option
                    key={s}
                    value={s}
                  >
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-full px-2 pb-2'>
              {error !== '' ? (
                <div className='text-left text-red-500 font-semibold text-md mb-2 tracking-wide'>{error}</div>
              ) : (
                <div className='py-4'></div>
              )}
              <button
                type='submit'
                className='btn-submit'
                disabled={isLoading}
              >
                <span>{matchId ? 'Save Changes' : 'Add Work'}</span>
                <span>
                  {isLoading === false ? (
                    <HiArrowNarrowRight />
                  ) : (
                    <Spinner
                      size={14}
                      className='ml-2'
                      animating={isLoading}
                    />
                  )}
                </span>
              </button>
            </div>
          </div>
        </form>
      </section>
    </AppLayout>
  )
}

export default AddMatch
